import AlertOutline from 'assets/icons/AlertOutline';
import CheckOutline from 'assets/icons/CheckOutline';
import HandleX from 'assets/icons/HandleX';
import Refresh from 'assets/icons/Refresh';
import { SyncOff, SyncOn } from 'assets/icons/SyncOn';
import Chip from 'common/components/Chip';
import Tooltip from 'common/components/Tooltip';
import { formatDate } from 'common/utils';
import { isEmpty, isNil } from 'lodash';
import React from 'react';

const DEFAULT_MAX_SYNC_AGE_HOURS = 6;
const SAPSF_INTEGRATION_SUITE_MAX_SYNC_AGE_HOURS = 24;
const SYNCABLE_SOURCES = ['SAPSF', 'WORKABLE'];

const SYNC_STATUS = {
  resyncing: {
    chipVariant: 'warning',
    icon: <Refresh size='14px' color='#ED9300' />,
    tooltip: { show: true, label: 'sync-started', date: null }, // show last_attempt_sync
    status_label: 'resyncing',
  },
  never_sync: {
    chipVariant: 'alert',
    icon: <HandleX size='14px' />,
    tooltip: { show: false, label: '', date: null },
    status_label: 'never-sync',
  },
  synced: {
    chipVariant: 'success',
    icon: <CheckOutline size='14px' strokeColor='#59BC7D' />,
    tooltip: { show: true, label: 'last-sync', date: null }, // show last_successful_sync
    status_label: 'synced',
  },
  out_of_sync: {
    chipVariant: 'primary',
    icon: <AlertOutline size='14px' color='#F16347' />,
    tooltip: { show: true, label: 'last-attempt-sync', date: null }, // show last_attempt_sync || last_successful_sync
    status_label: 'out-of-sync',
  },
  awaiting_first_sync: {
    chipVariant: 'warning',
    icon: <AlertOutline size='14px' color='#ED9300' />,
    tooltip: { show: false, label: '', date: null },
    status_label: 'awaiting-first-sync',
  },
};

const buildSyncStatus = (statusConfig, tooltipDate, i18n, labelOverride) => {
  return {
    ...statusConfig,
    tooltip: {
      ...statusConfig.tooltip,
      label: labelOverride ?? statusConfig.tooltip.label,
      date: tooltipDate ? formatDate(tooltipDate, i18n, 'long', true) : null,
    },
  };
};

const isLastSyncWithin = (
  lastSuccessfulSync,
  hours = DEFAULT_MAX_SYNC_AGE_HOURS,
) => {
  const lastSync = new Date(lastSuccessfulSync).getTime();
  const now = Date.now();
  return now - lastSync < hours * 60 * 60 * 1000;
};

const getMaxSyncAgeHours = (source) => {
  const isSapsfIntegrationSuite =
    source?.name === 'SAPSF' && source?.is_sapsf_integration_suite === true;
  return isSapsfIntegrationSuite
    ? SAPSF_INTEGRATION_SUITE_MAX_SYNC_AGE_HOURS
    : DEFAULT_MAX_SYNC_AGE_HOURS;
};

/*
State machine for resolveSyncStatusState

IF NOT is_sync_ready || isManualSyncStarted
  -> STATE = "resyncing"
  -> show tooltip: last_attempt_sync

ELSE IF last_successful_sync == null
  -> STATE = "never_sync"
  -> NOT show tooltip

ELSE IF now - last_successful_sync < max_sync_age_hours
  -> STATE = "synced"
  -> show tooltip: last_successful_sync

ELSE
  -> STATE = "out_of_sync"
  -> show tooltip: last_attempt_sync ? last_attempt_sync : last_successful_sync
*/

const resolveSyncStatusState = (source, isManualSyncStarted, i18n) => {
  const maxSyncAgeHours = getMaxSyncAgeHours(source);

  if (isManualSyncStarted) {
    return buildSyncStatus(
      SYNC_STATUS.resyncing,
      new Date().toISOString(),
      i18n,
    );
  }

  if (source.is_periodic_sync && !source.last_successful_sync) {
    return buildSyncStatus(SYNC_STATUS.awaiting_first_sync, null, i18n);
  }

  if (!source?.is_sync_ready) {
    return buildSyncStatus(
      SYNC_STATUS.resyncing,
      source.last_attempt_sync,
      i18n,
    );
  }

  if (!source.last_successful_sync) {
    return SYNC_STATUS.never_sync;
  }

  if (isLastSyncWithin(source.last_successful_sync, maxSyncAgeHours)) {
    return buildSyncStatus(
      SYNC_STATUS.synced,
      source.last_successful_sync,
      i18n,
    );
  }

  const tooltipDate = source.last_attempt_sync ?? source.last_successful_sync;
  const tooltipLabel = source.last_attempt_sync
    ? 'last-attempt-sync'
    : 'last-sync';
  return buildSyncStatus(
    SYNC_STATUS.out_of_sync,
    tooltipDate,
    i18n,
    tooltipLabel,
  );
};

const OfferSyncStatusBadge = ({
  externalSource,
  activeIntegration,
  chipVariantSufix,
  isManualSyncStarted = false,
  i18n,
  t,
}) => {
  // active integration === null || empty || NOT SYNCABLE SOURCE like VALIDATE_API  (DONT SHOW SYNC CHIP)
  const isSyncAble =
    !isNil(activeIntegration) &&
    !isEmpty(activeIntegration) &&
    SYNCABLE_SOURCES.includes(activeIntegration);

  // active integration === SAPSF || WORKABLE -> external source ? resolveSyncStatusState : SYNC_STATUS.never_sync
  const status = externalSource
    ? resolveSyncStatusState(externalSource, isManualSyncStarted, i18n)
    : SYNC_STATUS.never_sync;

  const isPeriodicSync = externalSource?.is_periodic_sync;
  const { chipVariant, icon, tooltip, status_label } = status;

  const renderChipContent = () => {
    const variant = chipVariantSufix
      ? chipVariant + '-' + chipVariantSufix
      : chipVariant;
    return (
      <Chip variant={variant} className='text-sm'>
        {icon}
        {t(status_label)}
      </Chip>
    );
  };

  const renderSyncStatusIcon = () => {
    return isPeriodicSync ? (
      <SyncOn size={14} color='#8D91A0' />
    ) : (
      <SyncOff size={14} color='#8D91A0' />
    );
  };

  return isSyncAble ? (
    tooltip.show && tooltip.date ? (
      <Tooltip alignment='end' content={`${t(tooltip.label)} ${tooltip.date}`}>
        <div className='flex items-center gap-2'>
          {renderChipContent()}
          {renderSyncStatusIcon()}
        </div>
      </Tooltip>
    ) : (
      <div className='flex items-center gap-2'>
        {renderChipContent()}
        {renderSyncStatusIcon()}
      </div>
    )
  ) : null;
};

export default OfferSyncStatusBadge;
